import { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Printer, FileText, AlertTriangle, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import axiosInstance from '@/utils/axios';
import MedicationGroupByRoute, { createEmptyMedication } from './MedicationGroupByRoute';
import DateInput from './DateInput';
import { printPrescriptions } from '@/services/printService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Função para registrar estatísticas da receita avulsa
async function registerQuickPrescriptionStats(patientName, medicationGroups, createdBy) {
  try {
    const allMedications = medicationGroups.flatMap(group => 
      group.medicamentos
        .filter(med => med.name && med.name.trim())
        .map(med => ({
          name: med.name,
          dosage: med.dosage || null,
          dosage_unit: med.dosage_unit || 'MG',
          quantity: med.quantity || null,
          packaging_type: med.packaging_type || null
        }))
    );

    if (allMedications.length > 0) {
      await axios.post(`${API_URL}/api/quick-prescriptions/stats`, {
        patient_name: patientName,
        medications: allMedications,
        via: medicationGroups[0]?.via || 'ORAL',
        created_at: new Date().toISOString(),
        created_by: createdBy
      });
    }
  } catch (error) {
    console.error('Erro ao registrar estatísticas:', error);
    // Não bloquear a impressão se falhar
  }
}

// Função utilitária para gerar dados de impressão
// CORREÇÃO: Gera uma única receita com todos os grupos de via
function generatePrintData(patientName, patientBirthDate, medicationGroups, formatBirthDate) {
  const timestamp = Date.now();
  
  const patient = {
    id: 'avulso-' + timestamp,
    nome: patientName.toUpperCase(),
    data_nascimento: formatBirthDate(patientBirthDate)
  };

  // Filtra apenas grupos com medicamentos válidos
  const validGroups = medicationGroups
    .map(group => ({
      via: `USO ${group.via}`,
      medicamentos: group.medicamentos.filter(m => m.name.trim())
    }))
    .filter(group => group.medicamentos.length > 0);

  // Se não há medicamentos válidos, retorna vazio
  if (validGroups.length === 0) {
    return { patient, prescriptions: [] };
  }

  // Flatten todos os medicamentos com sua via
  const allMedicamentos = [];
  for (const group of validGroups) {
    for (const med of group.medicamentos) {
      allMedicamentos.push({
        name: med.name,
        dosage: med.dosage,
        dosage_unit: med.dosage_unit || 'MG',
        instructions: med.instructions,
        packaging_type: med.packaging_type,
        quantity: med.quantity,
        via: group.via
      });
    }
  }

  // Determina o uso: se múltiplas vias, lista todas; senão, usa a única via
  const uniqueVias = [...new Set(validGroups.map(g => g.via))];
  const uso = uniqueVias.length === 1 ? uniqueVias[0] : uniqueVias.join(' / ');

  // Gera UMA ÚNICA receita com todos os grupos
  const prescription = {
    id: `avulsa-${timestamp}`,
    medicamentos: allMedicamentos,
    medication_groups: validGroups, // Grupos para exibição organizada
    uso: uso,
    data_criacao: new Date(timestamp).toISOString(),
    data_impressao: new Date(timestamp).toISOString(),
    data_validade: new Date(timestamp + 60 * 24 * 60 * 60 * 1000).toISOString()
  };

  return { patient, prescriptions: [prescription] };
}

export default function QuickPrescriptionModal({ open, onClose }) {
  const [patientName, setPatientName] = useState('');
  const [patientBirthDate, setPatientBirthDate] = useState('');
  const [printData, setPrintData] = useState(null);
  const [showQRCode, setShowQRCode] = useState(true); // Estado para QR Code
  const [canGenerateQRCode, setCanGenerateQRCode] = useState(false); // Permissão de QR Code
  const [prescriptionName, setPrescriptionName] = useState(''); // Nome personalizado da receita
  
  // Ref para armazenar dados de impressão
  const printDataRef = useRef({ patient: null, prescriptions: [] });
  
  // Novo estado: grupos de medicamentos por via (igual aos outros modais)
  const [medicationGroups, setMedicationGroups] = useState([
    {
      via: 'ORAL',
      medicamentos: [createEmptyMedication()]
    }
  ]);

  // Verificar permissão para gerar QR Code ao abrir modal
  useEffect(() => {
    if (open) {
      checkQRCodePermission();
    }
  }, [open]);

  const checkQRCodePermission = async () => {
    try {
      const response = await axiosInstance.get('/auth/can-generate-qrcode');
      setCanGenerateQRCode(response.data.can_generate);
      // Se não tem permissão, desabilita QR Code por padrão
      if (!response.data.can_generate) {
        setShowQRCode(false);
      }
    } catch (error) {
      console.error('Erro ao verificar permissão de QR Code:', error);
      setCanGenerateQRCode(false);
      setShowQRCode(false);
    }
  };

  // Formatar data de nascimento para exibição
  const formatBirthDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  }, []);

  const handlePrint = useCallback(async () => {
    if (!patientName.trim()) {
      toast.error('Informe o nome do paciente');
      return;
    }
    
    // Validar que há pelo menos um medicamento válido
    const hasValidMedication = medicationGroups.some(group => 
      group.medicamentos.some(med => med.name.trim() !== '')
    );
    
    if (!hasValidMedication) {
      toast.error('Adicione pelo menos um medicamento');
      return;
    }
    
    // Gerar dados de impressão
    const data = generatePrintData(patientName, patientBirthDate, medicationGroups, formatBirthDate);
    printDataRef.current = data;
    setPrintData(data);
    
    // Registrar estatísticas da receita avulsa
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    await registerQuickPrescriptionStats(patientName, medicationGroups, currentUser?.id || null);
    
    // Usar novo sistema de impressão por janela
    const items = data.prescriptions.map(p => ({ patient: data.patient, prescription: p }));
    printPrescriptions(items, { showQRCode: showQRCode && canGenerateQRCode, isAvulsa: true });
  }, [patientName, patientBirthDate, medicationGroups, formatBirthDate, showQRCode, canGenerateQRCode]);

  const handleClose = () => {
    onClose();
  };

  const handleReset = () => {
    setPatientName('');
    setPatientBirthDate('');
    setPrescriptionName('');
    setMedicationGroups([
      {
        via: 'ORAL',
        medicamentos: [createEmptyMedication()]
      }
    ]);
    setPrintData(null);
    // Manter showQRCode baseado na permissão
    if (canGenerateQRCode) {
      setShowQRCode(true);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Receita Avulsa (Impressão Rápida)
            </DialogTitle>
            <DialogDescription>
              Crie uma receita para impressão imediata. Esta receita NÃO será salva no sistema.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Aviso */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-800 font-medium">
                  Receita Avulsa - Apenas para impressão
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Esta receita não será registrada no sistema e não ficará vinculada a nenhum paciente cadastrado.
                </p>
              </div>
            </div>

            {/* Dados do Paciente */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              <h3 className="font-semibold text-gray-700">Dados do Paciente</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="patientName">Nome do Paciente *</Label>
                  <Input
                    id="patientName"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value.toUpperCase())}
                    placeholder="NOME COMPLETO DO PACIENTE"
                    className="mt-1 h-12 bg-white"
                    data-testid="quick-rx-patient-name"
                  />
                </div>
                <div>
                  <Label htmlFor="patientBirthDate">Data de Nascimento (opcional)</Label>
                  <DateInput
                    id="patientBirthDate"
                    value={patientBirthDate}
                    onChange={setPatientBirthDate}
                    className="mt-1 h-12 bg-white"
                    data-testid="quick-rx-patient-birth"
                  />
                </div>
              </div>
              
              {/* Nome da Receita */}
              <div>
                <Label htmlFor="prescriptionName">Nome da Receita (opcional)</Label>
                <Input
                  id="prescriptionName"
                  value={prescriptionName}
                  onChange={(e) => setPrescriptionName(e.target.value)}
                  placeholder="Ex: Medicação Contínua, Receita Hipertensão..."
                  className="mt-1 h-10 bg-white"
                  data-testid="quick-rx-prescription-name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Um nome ajuda a identificar a receita na impressão
                </p>
              </div>
            </div>

            {/* Medicamentos agrupados por via (mesmo layout dos outros modais) */}
            <MedicationGroupByRoute
              groups={medicationGroups}
              onChange={setMedicationGroups}
              dataTestIdPrefix="quick-rx-"
            />

            {/* Toggle QR Code - Só aparece se usuário tem permissão */}
            {canGenerateQRCode && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-gray-600" />
                  <Label htmlFor="qr-toggle-quick" className="text-sm text-gray-700">
                    Incluir QR Code de validação na impressão
                  </Label>
                </div>
                <Switch
                  id="qr-toggle-quick"
                  checked={showQRCode}
                  onCheckedChange={setShowQRCode}
                />
              </div>
            )}

            {/* Ações */}
            <div className="flex items-center justify-between pt-4 border-t">
              <Button 
                type="button"
                variant="outline" 
                onClick={handleReset} 
                className="rounded-full"
              >
                Limpar Formulário
              </Button>
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={handleClose} 
                  className="rounded-full"
                >
                  Cancelar
                </Button>
                <Button 
                  type="button"
                  onClick={handlePrint} 
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="quick-rx-print-button"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Receita
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
